import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { HorizontalLayout } from "./components/layout/HorizontalLayout";
import { PermissionGate } from "./components/auth/PermissionGate";
import { LoginPage } from "./pages/LoginPage";
import { CrmBridgePage } from "./pages/CrmBridgePage";
import lazy from "./lib/lazyWithRetry";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";

// Lazy-loaded pages for faster initial load
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const LeadProfilePage = lazy(() => import("./pages/LeadProfilePage"));
const LocationsPage = lazy(() => import("./pages/admin/LocationsPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const RolesPage = lazy(() => import("./pages/admin/RolesPage"));
const AppointmentTypesPage = lazy(() => import("./pages/admin/AppointmentTypesPage"));
const NotBookedReasonsPage = lazy(() => import("./pages/admin/NotBookedReasonsPage"));
const WebhooksPage = lazy(() => import("./pages/admin/WebhooksPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const SchedulingPage = lazy(() => import("./pages/SchedulingPage"));
const CallTrackerPage = lazy(() => import("./pages/CallTrackerPage"));
const BookingLogsPage = lazy(() => import("./pages/BookingLogsPage"));
const OverridesPage = lazy(() => import("./pages/OverridesPage"));
const AvailabilityPage = lazy(() => import("./pages/AvailabilityPage"));
const CalendarActivityLogPage = lazy(() => import("./pages/CalendarActivityLogPage").then(m => ({ default: m.CalendarActivityLogPage })));
const SchedulingAnalyticsPage = lazy(() => import("./pages/SchedulingAnalyticsPage"));
const FrontDeskTasksPage = lazy(() => import("./pages/FrontDeskTasksPage"));
const ReportedCallLogsPage = lazy(() => import("./pages/admin/ReportedCallLogsPage"));
const InboxPage = lazy(() => import("./pages/inbox/InboxPage"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));
const ComplianceFeedPage = lazy(() => import("./pages/ComplianceFeedPage"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPages").then(m => ({ default: m.PlaceholderPage })));
const ConsentRedirectPage = lazy(() => import("./pages/ConsentRedirectPage"));
const ConsentSettingsPage = lazy(() => import("./pages/admin/ConsentSettingsPage"));
const BannerManagementPage = lazy(() => import("./pages/admin/BannerManagementPage").then(m => ({ default: m.BannerManagementPage })));
const CompetitiveEdgePage = lazy(() => import("./pages/admin/CompetitiveEdgePage").then(m => ({ default: m.CompetitiveEdgePage })));
const HubSpotEdgePage = lazy(() => import("./pages/admin/HubSpotEdgePage").then(m => ({ default: m.HubSpotEdgePage })));
const ActivityHistoryPage = lazy(() => import("./pages/ActivityHistoryPage"));
const PatientsListPage = lazy(() => import("./pages/PatientsListPage"));
const PatientProfilePage = lazy(() => import("./pages/PatientProfilePage"));
const CustomLinkPage = lazy(() => import("./pages/CustomLinkPage"));

function App() {
  return (
    <ChunkErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <RouteTracker />
        <EmbeddedRestoreOnLoad />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/competitive-edge" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>}><PublicCompetitiveEdge /></Suspense>} />
          <Route path="/competitive-edge/hubspot" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>}><PublicHubSpotEdge /></Suspense>} />
          <Route path="/consent" element={<Suspense fallback={<div />}><ConsentRedirectPage /></Suspense>} />

          <Route element={<HorizontalLayout />}>
            <Route path="/" element={<RestoreLastRoute />} />
            <Route path="/dashboard" element={<PermissionGate module="stats" action="view_hq_data" redirect><DashboardPage /></PermissionGate>} />
            <Route path="/leads" element={<PermissionGate module="leads" action="view" redirect><LeadsPage /></PermissionGate>} />
            <Route path="/leads/:leadId" element={<PermissionGate module="leads" action="view" redirect><LeadProfilePage /></PermissionGate>} />
            <Route path="/front-desk-tasks" element={<PermissionGate module="front_desk_tasks" action="view" redirect><FrontDeskTasksPage /></PermissionGate>} />
            <Route path="/call-tracker" element={<PermissionGate module="call_tracker" action="view_calls" redirect><CallTrackerPage /></PermissionGate>} />
            <Route path="/custom/:linkId" element={<Suspense fallback={<div className="p-8"><div className="animate-spin h-6 w-6 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>}><CustomLinkPage /></Suspense>} />
            <Route path="/scheduling" element={<PermissionGate module="scheduling" action="view_calendar" redirect><SchedulingPage /></PermissionGate>} />
            <Route path="/booking-logs" element={<PermissionGate module="scheduling" action="view_calendar" redirect><BookingLogsPage /></PermissionGate>} />
            <Route path="/overrides" element={<PermissionGate module="scheduling" action="manage_overrides" redirect><OverridesPage /></PermissionGate>} />
            <Route path="/availability" element={<PermissionGate module="scheduling" action="manage_availability" redirect><AvailabilityPage /></PermissionGate>} />
            <Route path="/scheduling-activity-log" element={<PermissionGate module="scheduling" action="manage_availability" redirect><CalendarActivityLogPage /></PermissionGate>} />
            <Route path="/scheduling-analytics" element={<PermissionGate module="scheduling" action="view_analytics" redirect><SchedulingAnalyticsPage /></PermissionGate>} />

            {/* Admin Routes */}
            <Route path="/admin/locations" element={<PermissionGate module="settings" action="manage_locations" redirect><LocationsPage /></PermissionGate>} />
            <Route path="/admin/users" element={<PermissionGate module="settings" action="*" redirect><UsersPage /></PermissionGate>} />
            <Route path="/admin/roles" element={<PermissionGate module="settings" action="roles_permissions" redirect><RolesPage /></PermissionGate>} />
            <Route path="/admin/appointment-types" element={<PermissionGate module="settings" action="appointment_settings" redirect><AppointmentTypesPage /></PermissionGate>} />
            <Route path="/admin/not-booked-reasons" element={<PermissionGate module="settings" action="appointment_settings" redirect><NotBookedReasonsPage /></PermissionGate>} />
            <Route path="/admin/webhooks" element={<PermissionGate module="settings" action="manage_users" redirect><WebhooksPage /></PermissionGate>} />
            <Route path="/admin/settings" element={<PermissionGate module="settings" action="*" redirect><SettingsPage /></PermissionGate>} />
            <Route path="/admin/reported-calls" element={<PermissionGate module="settings" action="manage_users" redirect><ReportedCallLogsPage /></PermissionGate>} />
            <Route path="/admin/consent-settings" element={<PermissionGate module="settings" action="manage_users" redirect><ConsentSettingsPage /></PermissionGate>} />
            <Route path="/admin/compliance-feed" element={<PermissionGate module="settings" action="manage_users" redirect><ComplianceFeedPage /></PermissionGate>} />
            <Route path="/admin/banners" element={<PermissionGate module="settings" action="manage_banners" redirect><BannerManagementPage /></PermissionGate>} />
            <Route path="/admin/crm-bridge" element={<CrmBridgePage />} />
            <Route path="/patients" element={<PatientsListPage />} />
            <Route path="/patients/:contactId" element={<PatientProfilePage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/activity-history" element={<ActivityHistoryPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/global-list" element={<PlaceholderPage title="Global List" description="View all contacts across locations" />} />
          </Route>

          <Route path="*" element={<RestoreLastRoute />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
    </ChunkErrorBoundary>
  );
}

// Save current route to localStorage on every navigation
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path !== '/login' && path !== '/') {
      localStorage.setItem('last_route', path);
    }
  }, [location.pathname, location.search]);
  return null;
}

// Snapshot last_route at module load — BEFORE RouteTracker overwrites it during the
// initial render. This lets EmbeddedRestoreOnLoad read the user's true last position.
const INITIAL_LAST_ROUTE = (() => {
  try { return localStorage.getItem('last_route'); } catch { return null; }
})();

// Module-level flag — runs once per page load (iframe reload resets it)
let embeddedRestoreDone = false;

/**
 * When the iframe boots with ?embedded=1, restore the user's last visited route
 * (if different from current path). This lets the CRM Bridge menu link point to a
 * stable URL like /scheduling?embedded=1, while still preserving the user's
 * position when they switch between GHL custom menu links.
 *
 * - Fires ONLY on the initial mount of the app (per page load)
 * - Skipped on in-app navigation (RouteTracker keeps updating last_route)
 * - Skipped on /login (auth flow handles ?next= separately)
 */
function EmbeddedRestoreOnLoad() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (embeddedRestoreDone) return;
    embeddedRestoreDone = true;

    const params = new URLSearchParams(location.search || '');
    if (!params.has('embedded')) return;
    if (location.pathname === '/login') return;

    const lastRoute = INITIAL_LAST_ROUTE;
    if (!lastRoute || lastRoute === '/' || lastRoute === '/login') return;

    const lastPath = lastRoute.split('?')[0];
    if (lastPath === location.pathname) return; // Already where we want to be

    // Preserve embedded=1 across the redirect
    const sep = lastRoute.includes('?') ? '&' : '?';
    navigate(lastRoute + sep + 'embedded=1', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function getFirstAccessibleRoute() {
  // All users land on /scheduling after login, regardless of role.
  // PermissionGate will redirect them elsewhere if they lack scheduling access.
  return '/scheduling';
}

// Restore last visited route, or fallback to first accessible nav item
function RestoreLastRoute() {
  // Preserve query params (like ?embedded=1) through redirects
  const queryString = window.location.search || '';

  const saved = localStorage.getItem('last_route');
  if (saved && saved !== '/' && saved !== '/login') {
    // Append current query params if not already in saved route
    const target = saved.includes('?') ? saved : saved + queryString;
    return <Navigate to={target} replace />;
  }
  const firstRoute = getFirstAccessibleRoute();
  return <Navigate to={firstRoute + queryString} replace />;
}

export default App;

function PublicCompetitiveEdge() {
  return (
    <div className="min-h-screen bg-[#fafbfc]">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-[1440px] mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white text-base font-black shadow-lg shadow-emerald-500/30">O</div>
            <div>
              <span className="text-lg font-bold tracking-tight">OPTSC CRM</span>
              <span className="text-slate-400 text-xs ml-2 font-medium">Healthcare Operations Platform</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/competitive-edge/hubspot" className="text-[11px] text-orange-300 hover:text-orange-200 uppercase tracking-widest font-semibold">vs HubSpot →</a>
            <span className="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">Feature Comparison</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-[#fafbfc] pt-10 pb-20 px-8">
        <div className="max-w-[1440px] mx-auto text-center">
          <h1 className="text-4xl text-white tracking-tight" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', fontStyle: 'normal', fontWeight: 900 }}>Why clinics choose OPTSC</h1>
          <p className="text-lg text-slate-400 mt-3 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', fontStyle: 'normal', fontWeight: 400 }}>The only platform that combines call tracking, scheduling, inbox, task management, and AI — all in one system. See how we compare.</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1440px] mx-auto px-8 -mt-10">
        <CompetitiveEdgePage />
      </div>

      {/* Footer */}
      <div className="mt-16 py-8 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} OPTSC CRM. All rights reserved.</p>
      </div>
    </div>
  );
}

function PublicHubSpotEdge() {
  return (
    <div className="min-h-screen bg-[#fafbfc]" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontFeatureSettings: "'cv11', 'ss01'" }}>
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white text-base font-black shadow-lg shadow-emerald-500/30 flex-shrink-0">O</div>
            <div className="min-w-0">
              <span className="text-base sm:text-lg font-bold tracking-tight">OPTSC CRM</span>
              <span className="hidden sm:inline text-slate-400 text-xs ml-2 font-medium">Healthcare Operations Platform</span>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <a href="/competitive-edge" className="hidden sm:inline text-[11px] text-slate-300 hover:text-white uppercase tracking-widest font-semibold">All Comparisons</a>
            <span className="text-[10px] sm:text-[11px] text-amber-300 uppercase tracking-widest font-semibold">vs HubSpot</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <HubSpotEdgePage />
      </div>

      {/* Footer */}
      <div className="mt-12 sm:mt-16 py-6 sm:py-8 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} OPTSC CRM. All rights reserved.</p>
      </div>
    </div>
  );
}
